import React, { useState, useEffect, useRef } from 'react';
import './Home.css';
import StackedCards from './StackedCards';
import Navigation from './Navigation';
import { fetchSkills } from '../services/airtable';
// import LogoRow from "./LogoRow";

const Home = () => {
  const [skills, setSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const wavyRef = useRef(null);
  const arrowRef = useRef(null);

  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const svg = wavyRef.current;
          if (!svg) {
            ticking = false;
            return;
          }
          const path = svg.querySelector('#wavy-path');
          if (!path) {
            ticking = false;
            return;
          }
          const totalLength = path.getTotalLength();
          // Calculate scroll progress (0 to 1) for the hero section
          const hero = document.querySelector('.hero');
          if (!hero) {
            ticking = false;
            return;
          }
          const rect = hero.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          let progress = 1 - Math.max(0, Math.min(1, rect.bottom / windowHeight));
          // Animate the line
          path.style.strokeDasharray = totalLength;
          path.style.strokeDashoffset = totalLength * (1 - progress);

          // Move the arrow along the path as the line grows
          if (arrowRef.current) {
            const arrowLength = totalLength * progress;
            // Prevent the arrow from going past the end
            const cappedLength = Math.max(0, Math.min(arrowLength, totalLength));
            const point = path.getPointAtLength(cappedLength);
            // Get the direction of the path at this point for arrow rotation
            const prevPoint = path.getPointAtLength(Math.max(0, cappedLength - 1));
            const angle = Math.atan2(point.y - prevPoint.y, point.x - prevPoint.x) * 180 / Math.PI;
            arrowRef.current.setAttribute('transform', `translate(${point.x},${point.y}) rotate(${angle})`);
            // Show/hide arrow based on progress
            arrowRef.current.style.opacity = progress > 0.01 ? 1 : 0;
          }
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          // Scroll handling code can be added here if needed
          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch skills data
  useEffect(() => {
    const loadSkills = async () => {
      try {
        setSkillsLoading(true);
        const skillsData = await fetchSkills();
        console.log('Skills data loaded:', skillsData); // Debug log
        setSkills(skillsData);
      } catch (error) {
        console.error('Error loading skills:', error);
        // Fallback to static data if Airtable fails
        setSkills([
          { id: '1', title: 'Business Goal Alignment', description: 'Aligning digital initiatives with strategic business objectives to drive measurable outcomes', imageUrl: null },
          { id: '2', title: 'Customer Research', description: 'Conducting in-depth user research to uncover insights and validate product assumptions', imageUrl: null },
          { id: '3', title: 'Ideation Sessions', description: 'Facilitating collaborative workshops to generate innovative solutions and product ideas', imageUrl: null },
          { id: '4', title: 'Concept Prioritization & Roadmapping', description: 'Evaluating and prioritizing product concepts to create actionable development roadmaps', imageUrl: null }
        ]);
      } finally {
        setSkillsLoading(false);
      }
    };

    loadSkills();
  }, []);


  return (
    <div className="home">
      <Navigation />
      <section className="hero" id="home">
        <div className="hero-content-diagonal">
          <div className="diagonal-text">
            <h1>Andriana Skaperdas</h1>
            <p className="tagline">Digital Product Strategist</p>
            <h2>Bringing clarity to complex challenges</h2>
          </div>
          <div className="diagonal-visual">
            <div className="profile-photo-container">
              <div className="profile-photo">
                <img src={require('../assets/Headshot.png')} alt="Andriana Skaperdas - Digital Product Strategist" className="headshot-image" />
              </div>
            </div>
            <div className="resume-download-container">
              <a href="#contact" className="btn btn-primary">Download my resume</a>
            </div>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className="bio-simple">
        <div className="bio-simple-container">
          <h3>Nice to meet you</h3>
          <p>Whether I'm shaping a product vision, using AI to develop an app, or aligning a room full of differing opinions, I'm at my best when I'm solving problems, collaborating, and cutting through the noise. With my background in business strategy and information systems, I bridge the space between what's technically possible, what users care about, and what businesses are trying to achieve.</p>
          <p>Great strategy is so much more than a slide deck. It's about telling a story that aligns teams, drives action, and shows the art of what's possible. Outside of work, I bring that same creative energy into pottery and hands-on projects, passions which mirror my iterative and thoughtful approach to work.</p>
        </div>
      </section>


      {/* <LogoRow /> */}
      <StackedCards />
      <section className="bio-simple">
        <div className="bio-simple-container">
          <h3>About My Approach</h3>
          <p>I combine strategic thinking with practical implementation to help businesses create successful digital products. My methodology focuses on understanding user needs, market opportunities, and business objectives to deliver solutions that drive growth and innovation.</p>
        </div>
      </section>
      <section className="skills-section">
        <div className="work-grid">
          {skillsLoading ? (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem' }}>
              Loading skills...
            </div>
          ) : (
            skills.map((skill) => (
              <div key={skill.id} className="work-item">
                <div className="work-item-inner">
                  <div className="flip-indicator">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 7H17V10L21 6L17 2V5H5V11H7V7Z" fill="currentColor"/>
                      <path d="M17 17H7V14L3 18L7 22V19H19V13H17V17Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <div className="work-item-front">
                    <h2>{skill.title}</h2>
                    <p>{skill.description}</p>
                  </div>
                  <div className="work-item-back">
                    {skill.imageUrl ? (
                      <img
                        src={skill.imageUrl}
                        alt={skill.title}
                        className="work-item-image"
                        onError={(e) => {
                          console.error(`Failed to load image for ${skill.title}:`, skill.imageUrl);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={() => console.log(`Successfully loaded image for ${skill.title}`)}
                      />
                    ) : null}
                    <div
                      className="placeholder-image"
                      style={{ display: skill.imageUrl ? 'none' : 'flex' }}
                    >
                      <div className="placeholder-text">{skill.title}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
      <section className="mini-resume">
        <div className="mini-resume-content">
          <h2>Experience</h2>
          {/* Replace the example data below with your actual experience */}
          <div className="resume-entry">
            <div className="resume-company-row">
              <img src={require('../assets/Telus logo.jpg')} alt="TELUS Digital Logo" className="resume-logo" />
              <span className="resume-company"><strong>TELUS Digital</strong></span>
              <span className="resume-line"></span>
              <span className="resume-date">2025–Present</span>
            </div>
            <div className="resume-title">Lead Strategist</div>
          </div>
          <div className="resume-entry">
            <div className="resume-company-row">
              <img src={require('../assets/willowtree-logo.jpg')} alt="WillowTree Logo" className="resume-logo" />
              <span className="resume-company"><strong>WillowTree (now TELUS Digital)</strong></span>
              <span className="resume-line"></span>
              <span className="resume-date">2023–2025</span>
            </div>
            <div className="resume-title">Senior Digital Product Strategist</div>
          </div>
          <div className="resume-entry">
            <div className="resume-company-row">
              <img src={require('../assets/ey-logo.png')} alt="EY Logo" className="resume-logo" />
              <span className="resume-company"><strong>Ernst & Young</strong></span>
              <span className="resume-line"></span>
              <span className="resume-date">2021–2023</span>
            </div>
            <div className="resume-title">User Experience Technology Consultant</div>
          </div>
          <div className="resume-entry">
            <div className="resume-company-row">
              <img src={require('../assets/takeda-logo.png')} alt="Takeda Logo" className="resume-logo" />
              <span className="resume-company"><strong>Takeda Pharmaceuticals</strong></span>
              <span className="resume-line"></span>
              <span className="resume-date">2020</span>
            </div>
            <div className="resume-title">Enterprise Architecture Intern</div>
          </div>
          {/* Education Section */}
          <h2 className="education-title">Education</h2>
          <div className="resume-entry">
            <div className="resume-company-row">
              <img src={require('../assets/bu-logo.png')} alt="Boston University Logo" className="resume-logo" />
              <span className="resume-company"><strong>Boston University Questrom School of Business</strong></span>
              <span className="resume-line"></span>
              <span className="resume-date">2017–2021</span>
            </div>
            <div className="resume-title">B.S. in Business Administraction, Concentrations in Management Information Systems and Strategy</div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home; 